import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useEnhancePrompt } from './useEnhancePrompt';

export interface UsePromptEnhancementReturn {
  handleEnhance: () => Promise<void>;
  isEnhancing: boolean;
  resetAfterSubmit: () => void;
  showEnhancementHint: boolean;
  showUndoHint: boolean;
}

/**
 * Manages the full prompt-enhancement lifecycle:
 * - Streaming enhanced text character-by-character
 * - Tracking undo state so the user can revert with Escape
 * - Deriving `hasVariables` and `showEnhancementHint` from the current text
 */
export const usePromptEnhancement = (
  text: string,
  setText: React.Dispatch<React.SetStateAction<string>>,
  textareaRef: React.RefObject<HTMLTextAreaElement | null>,
): UsePromptEnhancementReturn => {
  const { t } = useTranslation();
  const [originalText, setOriginalText] = useState('');
  const [enhancedText, setEnhancedText] = useState('');
  const [showUndoHint, setShowUndoHint] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const streamingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isMountedRef = useRef(true);
  const enhancePrompt = useEnhancePrompt();

  const hasVariables = useMemo(() => /\[[A-Z\s]+\]/.test(text), [text]);
  const wordCount = text.trim().split(/\s+/).filter(Boolean).length;
  const showEnhancementHint = wordCount >= 3 && !showUndoHint && !hasVariables && !isEnhancing;

  // Clear undo state when user edits after enhancement
  useEffect(() => {
    if (showUndoHint && enhancedText && text !== enhancedText && !isEnhancing) {
      setShowUndoHint(false);
      setOriginalText('');
      setEnhancedText('');
    }
  }, [text, enhancedText, showUndoHint, isEnhancing]);

  // Escape key → undo enhancement (only when textarea is focused)
  useEffect(() => {
    if (!originalText || !showUndoHint) {
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key !== 'Escape' ||
        document.activeElement !== textareaRef.current ||
        !originalText ||
        !showUndoHint
      ) {
        return;
      }

      // stopPropagation prevents Outlook's window-level Escape handler
      // (which closes the task pane) from firing after our undo.
      // capture: true ensures we intercept before any bubble-phase handlers.
      e.preventDefault();
      e.stopPropagation();
      setText(originalText);
      setOriginalText('');
      setEnhancedText('');
      setShowUndoHint(false);
    };

    window.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, [originalText, showUndoHint, setText, textareaRef]);

  // Cleanup on unmount: cancel stream and mark component as unmounted
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (streamingIntervalRef.current) {
        clearInterval(streamingIntervalRef.current);
      }
    };
  }, []);

  const streamText = useCallback(
    (enhanced: string) => {
      // Cancel any in-flight stream before starting a new one
      if (streamingIntervalRef.current) {
        clearInterval(streamingIntervalRef.current);
        streamingIntervalRef.current = null;
      }

      return new Promise<void>((resolve) => {
        let index = 0;
        setText('');

        const interval = setInterval(() => {
          if (!isMountedRef.current) {
            clearInterval(interval);
            resolve();
            return;
          }
          if (index < enhanced.length) {
            setText(enhanced.slice(0, index + 1));
            index++;
          } else {
            clearInterval(interval);
            streamingIntervalRef.current = null;
            resolve();
          }
        }, 15);

        // Assign ref after setInterval so clearInterval in the cancel path is safe,
        // but before the first tick fires to avoid a gap.
        streamingIntervalRef.current = interval;
      });
    },
    [setText],
  );

  const handleEnhance = useCallback(async () => {
    const trimmed = text.trim();
    if (!trimmed) {
      toast.error(t('assistant.promptEnhancement.enterText'));
      return;
    }

    const wc = trimmed.split(/\s+/).filter(Boolean).length;
    if (wc < 3) {
      toast.error(t('assistant.promptEnhancement.minWords'));
      return;
    }

    if (enhancePrompt.isPending || isEnhancing) {
      return;
    }

    setOriginalText(text);
    setIsEnhancing(true);

    try {
      const result = await enhancePrompt.mutateAsync(trimmed);

      if (!result?.enhanced) {
        toast.error(t('assistant.promptEnhancement.failed'));
        setOriginalText('');
        setIsEnhancing(false);
        return;
      }

      if (result.enhanced.trim() === trimmed) {
        toast.info(t('assistant.promptEnhancement.wellWritten'));
        setOriginalText('');
        setIsEnhancing(false);
        return;
      }

      await streamText(result.enhanced);

      if (!isMountedRef.current) {
        return;
      }

      setEnhancedText(result.enhanced);
      setShowUndoHint(true);
      setIsEnhancing(false);

      setTimeout(() => textareaRef.current?.focus(), 0);

      if (result.variables && result.variables.length > 0) {
        toast.success(t('assistant.promptEnhancement.success'), { duration: 4000 });
      }
    } catch (error: unknown) {
      let errorMessage = t('assistant.promptEnhancement.errors.generic');

      if (error instanceof Error) {
        if (error.message.includes('Rate limit')) {
          errorMessage = t('assistant.promptEnhancement.errors.tooManyRequests');
        } else if (error.message.includes('timed out')) {
          errorMessage = t('assistant.promptEnhancement.errors.timeout');
        } else if (error.message.includes('network') || error.message.includes('fetch')) {
          errorMessage = t('assistant.promptEnhancement.errors.networkError');
        } else if (error.message.includes('500') || error.message.includes('Server error')) {
          errorMessage = t('assistant.promptEnhancement.errors.serverError');
        }
      }

      toast.error(errorMessage);
      setOriginalText('');
      setIsEnhancing(false);
    }
  }, [text, enhancePrompt, isEnhancing, streamText, t, textareaRef]);

  const resetAfterSubmit = useCallback(() => {
    setOriginalText('');
    setEnhancedText('');
    setShowUndoHint(false);
  }, []);

  return {
    handleEnhance,
    isEnhancing,
    resetAfterSubmit,
    showEnhancementHint,
    showUndoHint,
  };
};
